import {
  Exclude,
  instanceToPlain,
  plainToInstance,
  Transform,
  TransformationType,
} from 'class-transformer';
import { Util } from '../util';
import { FilingTypeKey } from './filing-type-key.enum';

function TransformYesNo() {
  return Transform(({ value, type }) => {
    switch (type) {
      case TransformationType.PLAIN_TO_CLASS:
        return value === true || value === 'Yes';
      case TransformationType.CLASS_TO_PLAIN:
        return value === true || value === 'Yes' ? 'Yes' : 'No';
      default:
        return value;
    }
  });
}

export interface ExtensionApprovalAuthority {
  role: string;
  notify: boolean;
}

export class BaseGroupConfigs {
  // EFEDS-6263 bulk action delete notification
  @TransformYesNo()
  bulkActionRecordsManagementNotification = false;
  bulkActionRecordsManagementRecipients = '';
  bulkActionRecordsManagementText =
    'A bulk records management request has been initiated by [[bulkActionUsername]] ([[bulkActionUserEmail]]) for one or more reports in your agency. The request will be processed if not canceled by [[bulkActionExecutionDateTime]]. Please visit the Records Management screen to find the affected reports by using the Customize Display feature to reveal the Pending Purge and Pending Deletion columns, then use the sort controls to show those rows with Yes in either column.';
  bulkActionRecordsManagementCancelationText =
    'A previously scheduled bulk management request for one or more reports in your agency has been canceled by [[bulkActionCancelUsername]] ([[bulkActionCancelUserEmail]]). The original request was initiated by [[bulkActionUsername]] ([[bulkActionUserEmail]]) at [[bulkActionRequestDateTime]].';

  public static createFromJson(json: any): BaseGroupConfigs {
    return plainToInstance(BaseGroupConfigs, json);
  }

  public toJSON(): any {
    return instanceToPlain(this);
  }
}

export class AgencyConfigs extends BaseGroupConfigs {
   // OGE-9494 text shared by regular and designee user provisioning notices
  static userProvisioningNoticeCommonBody = 
    `, the system created by the U.S. Office of Government Ethics for electronically filing and reviewing public financial disclosure reports.\n
Logging into Integrity requires an account with FedHub, a digital identity management system hosted by the U.S. Department of Health and Human Services, which provides secure account management services for Integrity.\n
Whether you are a current FedHub user or not, your first step is to visit Integrity.gov.\n
Your next step is determined by whether or not you have a government-issued PIV or CAC card.\n
If you have a PIV or CAC card, click the "Login with PIV or CAC (Preferred)" button on the Integrity landing page, then follow the prompts to log in. If you are prompted to associate your PIV or CAC card with an email address, use the email address where you received this message.\n
If you do not have a PIV or CAC card, click the "Login with Login.gov" button.\n
If you already have a Login.gov account, sign in using your existing Login.gov credentials. If not, click the "Create an Account" option and follow the prompts. Be sure to create your Login.gov account using the email address where you received this message. Because of the sensitive nature of the data in Integrity, you will be required to prove your identity during the Login.gov registration process.\n
Whether you logged in using PIV/CAC or using Login.gov, you should find yourself logged into Integrity, where you will acknowledge the user agreement before continuing to use the system.`;
  static userProvisioningNoticeCommonClosing = '\n\nIf you have any questions or problems, please contact your agency ethics official.';
  @TransformYesNo()
  allowExtensionRequest = false;
  // OGE-9197
  allowExtensionRequestOption = 'No';
  // OGE-8236
  allowExtensionRequestStartDate: string | null;
  allowExtensionRequestStopDate: string | null;
  isDoDagency: false;
  @TransformYesNo()
  requireJobDescription = false;
  @TransformYesNo()
  requireDoDnotice = false;
  @TransformYesNo()
  earlyTermination = false;
  @TransformYesNo()
  cascadeAllRoles = true;
  @TransformYesNo()
  displayIntermediateReviewers = false;
  @TransformYesNo()
  helpResource = false;
  helpResourceText?: string;
  helpResourceLink?: string;
  showEmailHelpLink = false;
  customSenderEmail = false;
  customSenderEmailAddress: string;
  useCc = false;
  ccEmailAddress?: (string | null)[];
  @TransformYesNo()
  userProvisioning = true;
  userProvisioningText =
    'Dear [[name]],\n\nYou have been granted access to use Integrity' +
    AgencyConfigs.userProvisioningNoticeCommonBody +
    AgencyConfigs.userProvisioningNoticeCommonClosing;
  @TransformYesNo()
  annualProfileUpdate = true;
  annualProfileUpdateText =
    'Please review your user profile to ensure that the information is still accurate. Out-of-date information may affect the processing of your report. You may access your profile by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  reportAssignment = true;
  reportAssignmentText =
    'Dear [[filer]],\n\n[[name]] has assigned you a report in Integrity at www.integrity.gov.';
  @TransformYesNo()
  assignmentReminder = true;
  assignmentReminderFrequency = 'every_7_days';
  assignmentReminderText =
    'This notice is a reminder that your [[year]] [[item]] report is due on [[date]]. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  pastDueReminder = false;
  pastDueReminderFrequency = 'every_7_days';
  pastDueReminderText =
    'This notice is a reminder that your [[year]] [[item]] report was due on [[date]]. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  amendReport = true;
  amendReportText =
    'Dear [[filer]],\n\n[[name]] has requested that you amend your [[year]] [[item]] filed [[date]]. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  amendReportReminder = true;
  amendReportReminderText =
    'Dear [[filer]],\n\n[[name]] has requested that you amend your [[year]] [[item]] filed [[date]]. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  certification = false;
  certificationText =
    'Your [[year]] [[item]] report has been certified by [[agency]]. You may view the certified copy of your report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  closes = false;
  closesText =
    'Your [[year]] [[item]] report has been closed without certification by [[agency]], www.integrity.gov. If you have any questions concerning this notice, please contact [to be entered by Agency].';
  @TransformYesNo()
  pendingAction = true;
  pendingActionText =
    'A [[year]] [[item]] report for [[name]] is pending your action as [[role]]. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  pendingActionReminder = true;
  pendingActionReminderFrequency = 'every_7_days';
  pendingActionReminderText =
    'A [[year]] [[item]] report for [[name]] is pending your action as [[role]]. The report has been pending for [[days]] days. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  specialUserProvisioning = true;
  specialUserProvisioningText =
    'Dear [[name]],\n\nYou have been registered as a Designee to [[filerName]] within Integrity' +
    AgencyConfigs.userProvisioningNoticeCommonBody +
    '\n\nIf you are a Designee for more than one other Integrity user, or if you hold any other role in Integrity, you will see a screen where you can select which role you want to use for this particular login session.' +
    AgencyConfigs.userProvisioningNoticeCommonClosing;
  @TransformYesNo()
  nomineeDraftReadyPPO = false;
  nomineeDraftReadyPPOText =
    '[[name]] submitted a report for review in Integrity, www.integrity.gov.';
  @TransformYesNo()
  nomineeDraftReadyWHC = false;
  nomineeDraftReadyWHCText =
    '[[name]] submitted a report for review in Integrity, www.integrity.gov.';
  @TransformYesNo()
  reviewerPendingAction = false;
  reviewerPendingActionText =
    'A [[year]] [[item]] report for [[name]] is pending your action as supervisor. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  reviewerPendingActionReminder = false;
  reviewerPendingActionReminderText =
    'A [[year]] [[item]] report for [[name]] is pending your action as [[role]]. The report has been pending for [[days]] days. You may access the report by logging into Integrity at www.integrity.gov.';
  ogePreclearanceText =
    'A nominee report for [[name]], [[agency]] has been precleared by OGE in Integrity at www.integrity.gov.';
  ogeCertificationText =
    'The [[year]] [[item]] report for [[name]] has been certified by OGE in Integrity at www.integrity.gov.';
  ogeNomineeCertificationText =
    'The [[year]] [[item]] report for [[name]] has been certified by OGE in Integrity at www.integrity.gov.';
  ogeClosesText =
    'The [[year]] [[item]] report for [[name]] has been closed without certification by OGE in Integrity at www.integrity.gov. If you have any questions concerning this notice, please contact [to be entered by OGE].';
  ogePreclearanceRoleRecipients: string[] = [];
  ogeCertificationRoleRecipients: string[] = [];
  ogeNomineeCertificationRoleRecipients: string[] = [];
  ogeClosesRoleRecipients: string[] = [];
  @TransformYesNo()
  filerExtensionRequest = false;
  filerExtensionRequestText =
    '[[name]] has requested an extension on a [[year]] [[item]] report for your approval. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  filerExtensionRequestWithdrawn = false;
  filerExtensionRequestWithdrawnText =
    '[[name]] has withdrawn their extension request for their [[year]] [[item]] report. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  filerExtensionRequestReminder = false;
  filerExtensionRequestReminderFrequency = 'every_7_days';
  filerExtensionRequestReminderText =
    '[[name]] has requested an extension on a [[year]] [[item]] report for your approval. You may access the report by logging into Integrity at www.integrity.gov.';
  @TransformYesNo()
  reviewerExtensionRequestDecision = true;
  reviewerExtensionRequestDecisionText =
    'Your request to extend your [[year]] [[item]] report by [[extension]] has been [[decision]] by [[reviewerName]]. [[comment]]. The new report due date is [[due]]. You may access the report by logging into Integrity at www.integrity.gov.';
  ogeLoginSecurityLink = '';
  minAuthLevelForNonFilers = '2';
  extensionReviewerRoles: {
    [key: string]: Array<string | ExtensionApprovalAuthority>;
  };

  public static createFromJson(json: any): AgencyConfigs {
    return plainToInstance(AgencyConfigs, json);
  }
}

export class OgeOversightConfigs extends AgencyConfigs {
  @TransformYesNo()
  hideLoginOn = false;
  @TransformYesNo()
  maintenanceNoticeOn = false;
  maintenanceNoticeHtml = '';
  @TransformYesNo()
  registerHtmlOn = true;
  registerHtml =
    'Not Registered in <em>Integrity?</em> Contact your agency if you are not registered <em>Integrity.</em> <a href="#">View agency contacts</a>';
  carouselHtml1 =
    '<p><em>Integrity</em> is the U.S. Office of Government Ethics system for electronically filing and reviewing Public Financial Disclosures (OGE Forms 278 and 278-T).</p><p class="hide"><a href="#">Learn more</a></p>';
  carouselHtml2 =
    '<h2>Report filing Deadlines</h2><ul><li>New entrant: 30 days after start date</li><li>Annual: May 15th</li><li>Termination: 30 days after separation</li><li>Periodic Transaction: the earlier of 45 days after the transaction or 30 days after receiving notice of the transaction</li></ul><p class="hide"><a href="#">Learn more</a></p>';
  carouselHtml3 =
    '<p>Contact your agency ethics official to request a filing extension or for more information about using Integrity.</p><p class="hide"><a href="#">Learn more</a></p>';
  @TransformYesNo()
  homeLinkOn = true;
  homeLink = '#';
  @TransformYesNo()
  learnLinkOn = true;
  learnLink = '#';
  @TransformYesNo()
  securityLinkOn = true;
  securityLink = '#';
  @TransformYesNo()
  privacyLinkOn = true;
  privacyLink = '#';
  @TransformYesNo()
  accessibilityLinkOn = true;
  accessibilityLink = '#';
  @TransformYesNo()
  helpLinkOn = true;
  helpLink = '#';

  public static createFromJson(json: any): OgeOversightConfigs {
    return plainToInstance(OgeOversightConfigs, json);
  }

  public toJSON(): any {
    return instanceToPlain(this);
  }
}

export class GroupConfigs extends BaseGroupConfigs {
  public static createFromJson(json: any): GroupConfigs {
    return plainToInstance(GroupConfigs, json);
  }

  public toJSON(): any {
    return instanceToPlain(this);
  }
}
